/**
 * Единый Error Mapper: технические ошибки → пользовательские сообщения (ADR-004).
 * Использовать только в момент отображения в UI. Не подменяет логирование.
 */

export const USER_ERROR_FALLBACK = 'Что-то пошло не так. Попробуйте ещё раз';
export const USER_ERROR_FALLBACK_ALT = 'Произошла ошибка. Попробуйте еще раз';

const normalizeError = (error) => {
    if (error == null) {
        return { message: '', code: '', status: null, details: '' };
    }
    if (typeof error === 'string') {
        return { message: error, code: '', status: null, details: '' };
    }
    return {
        message: error.message || error.error_description || error.msg || '',
        code: error.code || error.error || '',
        status: error.status ?? error.statusCode ?? null,
        details: error.details || error.hint || '',
    };
};

const mapAuthMessage = (message, code) => {
    const errorLower = message.toLowerCase();
    const codeLower = String(code || '').toLowerCase();

    // Supabase намеренно не различает "пользователь не найден" и "неверный пароль"
    if (errorLower.includes('invalid login credentials') || codeLower === 'invalid_credentials') {
        return 'Неверный email или пароль';
    }

    if (errorLower.includes('email not confirmed') || codeLower === 'email_not_confirmed') {
        return 'Email не подтвержден. Проверьте почту и подтвердите регистрацию';
    }

    if (errorLower.includes('user not found')) {
        return 'Пользователь не найден';
    }

    if (errorLower.includes('wrong password') || errorLower.includes('incorrect password')) {
        return 'Неверный пароль';
    }

    if (
        errorLower.includes('user already registered') ||
        errorLower.includes('email already registered') ||
        codeLower === 'signup_disabled' ||
        codeLower === 'email_address_not_authorized'
    ) {
        return 'Пользователь с таким email уже зарегистрирован';
    }

    if (
        errorLower.includes('password too weak') ||
        errorLower.includes('password should be') ||
        errorLower.includes('password does not meet') ||
        codeLower === 'weak_password'
    ) {
        if (message.includes('8') || errorLower.includes('eight')) {
            return 'Пароль должен содержать минимум 8 символов';
        }
        return 'Пароль слишком слабый. Используйте минимум 6 символов';
    }

    if (errorLower.includes('password is too short') || errorLower.includes('password must be')) {
        return 'Пароль должен содержать минимум 8 символов';
    }

    if (
        codeLower === 'same_password' ||
        errorLower.includes('same password') ||
        errorLower.includes('should be different')
    ) {
        return 'Новый пароль должен отличаться от старого пароля';
    }

    if (errorLower.includes('invalid email') || codeLower === 'invalid_email') {
        return 'Некорректный email адрес';
    }

    if (errorLower.includes('password required')) {
        return 'Пароль обязателен для заполнения';
    }

    if (errorLower.includes('email required')) {
        return 'Email обязателен для заполнения';
    }

    if (errorLower.includes('session expired') || errorLower.includes('token expired')) {
        return 'Сессия истекла. Войдите заново';
    }

    return null;
};

const mapByCodeAndText = (message, code, status, details) => {
    const errorLower = message.toLowerCase();
    const codeLower = String(code || '').toLowerCase();
    const detailsLower = String(details || '').toLowerCase();
    const blob = `${errorLower} ${codeLower} ${detailsLower}`;

    // Сеть
    if (
        errorLower.includes('network') ||
        errorLower.includes('fetch') ||
        errorLower.includes('failed to send a request') ||
        errorLower.includes('failed to fetch') ||
        codeLower === 'network_error'
    ) {
        return 'Ошибка сети. Проверьте подключение к интернету';
    }

    if (errorLower.includes('rate limit') || errorLower.includes('too many requests')) {
        return 'Слишком много запросов. Попробуйте позже';
    }

    // Нет данных (PostgREST)
    if (
        codeLower === 'pgrst116' ||
        errorLower.includes('0 rows') ||
        errorLower.includes('json object requested') ||
        errorLower.includes('multiple (or no) rows returned')
    ) {
        return 'Данные не найдены';
    }

    // RLS / доступ
    if (
        codeLower === '42501' ||
        status === 401 ||
        status === 403 ||
        blob.includes('permission denied') ||
        blob.includes('row-level security') ||
        blob.includes('rls') ||
        blob.includes('jwt expired') ||
        blob.includes('not authorized')
    ) {
        return 'Недостаточно прав для выполнения операции';
    }

    // Ограничения БД
    if (codeLower === '23505' || blob.includes('duplicate key') || blob.includes('unique constraint')) {
        return 'Такая запись уже существует';
    }

    if (codeLower === '23503' || blob.includes('foreign key')) {
        return 'Операция невозможна из‑за связанных данных';
    }

    if (codeLower === '22p02' || blob.includes('invalid input syntax')) {
        return 'Некорректные данные запроса';
    }

    // Auth (общие и специфичные)
    const authMapped = mapAuthMessage(message, code);
    if (authMapped) return authMapped;

    return null;
};

/**
 * Преобразует техническую ошибку в стабильное сообщение для UI.
 * @param {unknown} error - Error | Supabase/PostgREST error | string | null
 * @param {{ fallback?: string }} [options]
 * @returns {string}
 */
export const toUserMessage = (error, options = {}) => {
    const fallback = options.fallback || USER_ERROR_FALLBACK;
    const { message, code, status, details } = normalizeError(error);

    if (!message && !code && status == null) {
        return options.fallback || USER_ERROR_FALLBACK_ALT;
    }

    const mapped = mapByCodeAndText(message, code, status, details);
    if (mapped) return mapped;

    // Уже похоже на готовое пользовательское RU-сообщение (без технических маркеров)
    if (
        message &&
        !/[A-Z]{2,}[0-9]{2,}/.test(message) && // не PGRST116-like
        !/\b(select|insert|update|delete|from|where|violates|constraint|permission denied)\b/i.test(message) &&
        !/^[a-z_]+$/.test(message) && // не snake_case code
        /[а-яё]/i.test(message)
    ) {
        return message;
    }

    return fallback;
};

export default toUserMessage;
